import { PersistConfig, persistReducer } from 'redux-persist';
import { createReducer, getType } from 'typesafe-actions';

import { TiktokActions } from '@lib/state/_actions';
import { produce } from 'immer';
import storage from '@lib/state/storage';

import { TiktokUserInfo, TiktokPost } from '@lib/domain/tiktok';
import {
    TiktokState,
    TiktokCompleteOrder,
    TiktokOverviewOrder,
} from '@lib/state/Titkok/types';

const stateKey = 'tiktok';

/* ------------- Initial State ------------- */
const INITIAL_STATE: TiktokState = {
    user: undefined,
    posts: [],
    requestId: undefined,
    fetched: false,
    overviewOrder: {
        follower: {
            quantity: 0,
            price: 0,
            discount: 0,
        },
        like: {
            quantity: 0,
            price: 0,
            discount: 0,
        },
        view: {
            quantity: 0,
            price: 0,
            discount: 0,
        },
        share: {
            quantity: 0,
            price: 0,
            discount: 0,
        },
        comment: {
            quantity: 0,
            price: 0,
            discount: 0,
        },
    },
    completeOrder: {
        follower: {
            quantity: 0,
            price: 0,
        },
        like: {
            quantity: 0,
            price: 0,
        },
        view: {
            quantity: 0,
            price: 0,
            discount: 0,
        },
        share: {
            quantity: 0,
            price: 0,
            discount: 0,
        },
        comment: {
            quantity: 0,
            price: 0,
            discount: 0,
        },
        selectedPosts: [],
        total: 0,
    },
    cursor: undefined,
};

/* ------------- Reducers ------------- */
const setTiktokUser = (
    state: TiktokState,
    { payload: { user } }: ReturnType<typeof TiktokActions.setTiktokUser>,
): TiktokState =>
    produce(state, (draft) => {
        draft.user = user;
        draft.posts = [];
        draft.fetched = false;
        draft.overviewOrder = {
            follower: {
                quantity: 0,
                price: 0,
                discount: 0,
            },
            like: {
                quantity: 0,
                price: 0,
                discount: 0,
            },
            view: {
                quantity: 0,
                price: 0,
                discount: 0,
            },
            share: {
                quantity: 0,
                price: 0,
                discount: 0,
            },
            comment: {
                quantity: 0,
                price: 0,
                discount: 0,
            },
        };
        draft.completeOrder = {
            follower: {
                quantity: 0,
                price: 0,
            },
            like: {
                quantity: 0,
                price: 0,
            },
            view: {
                quantity: 0,
                price: 0,
                discount: 0,
            },
            share: {
                quantity: 0,
                price: 0,
                discount: 0,
            },
            comment: {
                quantity: 0,
                price: 0,
                discount: 0,
            },
            selectedPosts: [],
            total: 0,
        };
    });

const setTiktokPosts = (
    state: TiktokState,
    { payload: { posts } }: ReturnType<typeof TiktokActions.setTiktokPosts>,
): TiktokState =>
    produce(state, (draft) => {
        draft.posts = posts;
        draft.fetched = true;
    });

const setTiktokOverviewOrder = (
    state: TiktokState,
    {
        payload: { order },
    }: ReturnType<typeof TiktokActions.setTiktokOverviewOrder>,
): TiktokState =>
    produce(state, (draft) => {
        draft.overviewOrder = order;
    });

const setTiktokCompleteOrder = (
    state: TiktokState,
    {
        payload: { order },
    }: ReturnType<typeof TiktokActions.setTiktokCompleteOrder>,
): TiktokState =>
    produce(state, (draft) => {
        draft.completeOrder = order;
    });

const resetTiktokData = (): TiktokState => INITIAL_STATE;
const setTiktokCursorPost = (
    state: TiktokState,
    {
        payload: { cursor },
    }: ReturnType<typeof TiktokActions.setTiktokCursorPost>,
): TiktokState =>
    produce(state, (draft) => {
        draft.cursor = cursor;
        draft.fetched = true;
    });
/* ------------- Hookup Reducers To Types ------------- */
const reducer = createReducer(INITIAL_STATE, {
    [getType(TiktokActions.setTiktokUser)]: setTiktokUser,
    [getType(TiktokActions.setTiktokPosts)]: setTiktokPosts,
    [getType(TiktokActions.setTiktokOverviewOrder)]: setTiktokOverviewOrder,
    [getType(TiktokActions.setTiktokCompleteOrder)]: setTiktokCompleteOrder,
    [getType(TiktokActions.resetTiktokData)]: resetTiktokData,
    [getType(TiktokActions.setTiktokCursorPost)]: setTiktokCursorPost,
});

const persistConfig: PersistConfig<TiktokState> = {
    key: stateKey,
    storage,
};

const persistedReducer = persistReducer<
    TiktokState,
    TiktokActions.TiktokActions
>(persistConfig, reducer);

/* ------------- Selectors ------------- */
// use any for root state for now because of Dependency cycle error
// TODO: resolve dependency cycle error
// eslint-disable-next-line
const getReducerState = (state: any): TiktokState => state[stateKey];

const selectors = {
    getTiktokUser: (state: TiktokState): TiktokUserInfo | undefined =>
        state.user,
    getTiktokPosts: (state: TiktokState): TiktokPost[] => state.posts,
    getTiktokOverviewOrder: (state: TiktokState): TiktokOverviewOrder =>
        state.overviewOrder,
    getTiktokCompleteOrder: (state: TiktokState): TiktokCompleteOrder =>
        state.completeOrder,
    getRequestId: (state: TiktokState): string | undefined => state.requestId,
    getFetched: (state: TiktokState): boolean => state.fetched,
    getTiktokCursorPost: (state: TiktokState): string | undefined =>
        state.cursor,
};

/* ------------- Export ------------- */
const reducerObject = {
    // default export
    selectors,

    INITIAL_STATE,

    stateKey,
    getReducerState,
    reducer: persistedReducer,
};

export default reducerObject;
